/**
 * SEVESHOP — navigare in lightbox-ul de produs (ca la Ortosan).
 *
 * Tinker randeaza in `<dialog>` lista `.dialog-zoomed-gallery`. Cu CSS-ul din
 * `snippets/product-media-gallery-content.liquid` lista e un carusel orizontal cu
 * scroll-snap: o imagine pe ecran, `object-fit: contain`, swipe pe mobil.
 * Elementul asta adauga ce are Ortosan si Tinker nu: contor „1 / N", sageti
 * stanga/dreapta, navigare cu tastele si inchidere la clic pe fundal.
 *
 * Fara importuri din tema — modul independent.
 */
class SeveshopLightboxNav extends HTMLElement {
  /** Indexul curent, tinut explicit: latimea containerului e 0 cat timp dialogul
   *  se deschide (view transition), deci nu poate fi dedus mereu din scrollLeft. */
  #index = 0;

  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.gallery = this.dialog?.querySelector('.dialog-zoomed-gallery');
    if (!this.gallery) return;

    this.slides = [...this.gallery.children];
    this.counter = this.querySelector('[data-lb-current]');
    this.prevButton = this.querySelector('[data-lb-prev]');
    this.nextButton = this.querySelector('[data-lb-next]');

    // Fara requestAnimationFrame: trebuie sa mearga si cand pagina e ascunsa
    // (rAF e pus pe pauza), iar scrierile sunt cateva atribute, nu layout.
    this.onScroll = () => {
      const width = this.#slideWidth;
      if (width) this.#setIndex(Math.round(this.gallery.scrollLeft / width));

      /* Tema isi sincroniza miniaturile (aria-selected) si incarca varianta de
         rezolutie mare din `scroll`-ul DIALOGULUI. Cu carusel orizontal, cel care
         face scroll e lista, iar `scroll` nu bubble-uieste — deci ii trimitem
         evenimentul mai departe, ca sa nu reimplementam logica temei. */
      this.dialog?.dispatchEvent(new Event('scroll'));
    };
    this.gallery.addEventListener('scroll', this.onScroll, { passive: true });

    /* Clic pe o miniatura: tema face `targetImage.scrollIntoView()`, care pe un
       container cu scroll-snap avanseaza doar cativa pixeli. Ascultam clicul direct
       pe rail (nu evenimentul `zoom-media:selected`, care e emis si din scroll si
       ar trage carusel-ul inapoi in mijlocul unui swipe). */
    this.thumbnails = this.dialog?.querySelector('.dialog-thumbnails-list');
    this.onThumbnailClick = (event) => {
      const button = event.target.closest('.dialog-thumbnails-list__thumbnail');
      if (!button || !this.thumbnails) return;
      this.#scrollTo([...this.thumbnails.children].indexOf(button));
    };
    this.thumbnails?.addEventListener('click', this.onThumbnailClick);

    this.prevButton?.addEventListener('click', () => this.#go(-1));
    this.nextButton?.addEventListener('click', () => this.#go(1));

    this.onKeyDown = (event) => {
      if (!this.dialog?.open) return;
      if (event.key === 'ArrowLeft') this.#go(-1);
      if (event.key === 'ArrowRight') this.#go(1);
    };
    document.addEventListener('keydown', this.onKeyDown);

    // Inchidere la clic pe fundal (in jurul imaginii), nu pe imagine — tema
    // inchidea la clic pe imagine, ceea ce anula gestul de swipe.
    this.onGalleryClick = (event) => {
      if (event.target.closest('img, video, model-viewer, iframe, button')) return;
      this.#close();
    };
    this.gallery.addEventListener('click', this.onGalleryClick);

    // Dialogul se deschide de la indexul pe care s-a dat clic in galeria mica;
    // sincronizam contorul cand se deschide.
    this.observer = new MutationObserver(() => {
      if (this.dialog?.open) this.onScroll();
    });
    this.observer.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });

    this.#render();
  }

  disconnectedCallback() {
    this.gallery?.removeEventListener('scroll', this.onScroll);
    this.thumbnails?.removeEventListener('click', this.onThumbnailClick);
    this.gallery?.removeEventListener('click', this.onGalleryClick);
    document.removeEventListener('keydown', this.onKeyDown);
    this.observer?.disconnect();
  }

  /** Latimea unui slide. Slide-ul e `flex: 0 0 100%`, deci egala cu a containerului,
   *  dar o citim de pe slide pentru ca e corecta si inainte ca dialogul sa fie masurat. */
  get #slideWidth() {
    return this.slides[0]?.getBoundingClientRect().width || this.gallery.clientWidth || 0;
  }

  #go(direction) {
    this.#scrollTo(this.#index + direction);
  }

  #scrollTo(index) {
    const target = Math.min(this.slides.length - 1, Math.max(0, index));
    if (target === this.#index) return;

    const width = this.#slideWidth;
    if (width) {
      // `scrollTo` cu valoare explicita, nu `scrollIntoView`: pe un container cu
      // scroll-snap, scrollIntoView se opreste dupa cativa pixeli.
      this.gallery.scrollTo({ left: target * width, behavior: 'smooth' });
    } else {
      this.slides[target]?.scrollIntoView({ block: 'nearest', inline: 'start' });
    }

    this.#setIndex(target);
  }

  #setIndex(index) {
    const clamped = Math.min(this.slides.length - 1, Math.max(0, index));
    if (clamped === this.#index) return;
    this.#index = clamped;
    this.#render();
  }

  #render() {
    if (this.counter) this.counter.textContent = String(this.#index + 1);
    this.prevButton?.toggleAttribute('hidden', this.#index === 0);
    this.nextButton?.toggleAttribute('hidden', this.#index === this.slides.length - 1);
  }

  #close() {
    const closeButton = this.dialog?.querySelector('.dialog-zoomed-gallery__close-button');
    if (closeButton instanceof HTMLElement) closeButton.click();
    else this.dialog?.close();
  }
}

if (!customElements.get('seveshop-lightbox-nav')) {
  customElements.define('seveshop-lightbox-nav', SeveshopLightboxNav);
}
